import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Newspaper } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BlogLangSwitcher } from "@/components/BlogLangSwitcher";
import { Footer } from "@/components/Footer";
import { useLang } from "@/i18n";
import { setSEO } from "@/lib/seo";
import { articles, trArticle } from "@/data/blog";

const L = {
  fr: { title: "Le Blog I-LEASE", subtitle: "Actualités, fiscalité et conseils sur le leasing, le renting et la LLD en Belgique.", read: "Lire", home: "Accueil", seo: "Blog — I-LEASE SRL | Leasing, Renting & Fiscalité en Belgique" },
  nl: { title: "De I-LEASE Blog", subtitle: "Nieuws, fiscaliteit en advies over leasing, renting en LLD in België.", read: "Lezen", home: "Home", seo: "Blog — I-LEASE SRL | Leasing, Renting & Fiscaliteit in België" },
  en: { title: "The I-LEASE Blog", subtitle: "News, taxation and advice on leasing, renting and LTR in Belgium.", read: "Read", home: "Home", seo: "Blog — I-LEASE SRL | Leasing, Renting & Taxation in Belgium" },
};

export default function Blog() {
  const { lang } = useLang();
  const t = L[lang] || L.fr;
  useEffect(() => { window.scrollTo(0, 0); setSEO({ title: t.seo, description: t.subtitle, path: "/blog" }); }, [t.seo, t.subtitle]);

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo showEmblem={false} /></Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <BlogLangSwitcher />
            <Link to="/" className="text-sm text-muted-foreground hover:text-white flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> {t.home}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-1.5 mb-5">
          <Newspaper className="w-4 h-4 text-accent" strokeWidth={1.5} /><span className="text-sm text-accent">Blog</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold">{t.title}</h1>
        <p className="mt-4 text-muted-foreground text-lg max-w-2xl">{t.subtitle}</p>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {articles.map((a, i) => {
            const at = trArticle(a, lang);
            const locale = lang === "nl" ? "nl-BE" : lang === "en" ? "en-GB" : "fr-BE";
            return (
            <motion.div key={a.slug} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <Link to={`/blog/${a.slug}`} data-testid={`blog-list-${a.slug}`}
                className="group block rounded-2xl overflow-hidden border border-white/10 bg-card hover:-translate-y-1.5 transition-transform duration-300 h-full">
                <div className="relative h-48 overflow-hidden">
                  <img src={a.cover} alt={at.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-4 left-4 text-[11px] uppercase tracking-wider bg-accent text-white rounded-full px-3 py-1">{at.category}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span>{new Date(a.date).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span>·</span><span>{a.readTime}</span>
                  </div>
                  <h2 className="font-display text-lg font-bold leading-snug group-hover:text-accent transition-colors">{at.title}</h2>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{at.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent">{t.read} <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" strokeWidth={1.5} /></span>
                </div>
              </Link>
            </motion.div>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
